import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { displayTzNow } from "@/hooks/use-display-tz";
import { RELIABILITY_PATH, invalidateRunHistory } from "@/hooks/use-reliability";
import { SCHEDULES_PATH, schedulePath } from "@/hooks/use-schedules";
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import type {
  ForceResetBody,
  RetryRunBody,
  RetryRunResult,
  RetryTablesBody,
  RetryTablesResult,
  RunDetail,
  RunEventExportFormat,
  RunEventOrder,
  RunEventRow,
  RunEventsExportQuery,
  RunKind,
  RunStatus,
  RunSummaryRow,
  RunTransformResults,
  RunType,
  RunsInclude,
  StartRunBody,
  StartRunResponse,
  StepName,
} from "@shared/types/runs";
import { runStatusListParam } from "@shared/types/runs";
import { apiDownload, apiRequest, apiRequestEnvelope } from "@/lib/api";
import { isOffline, listQueryOptions } from "@/lib/listQuery";
import {
  STEP_ATTEMPT_EVENT_CODES,
  STEP_ATTEMPT_MAX_PAGES,
  foldStepAttempts,
  type StepAttemptHistory,
} from "@/lib/runs/stepAttempts";

/**
 * Run reads and writes (FR-52, FR-54, FR-56).
 *
 * Two things here are load-bearing and easy to get wrong.
 *
 * **The history is cursor paginated, not page numbered.** A run id carries its
 * timestamp in the MIDDLE (`{hex8}-{yyyymmddhhmmss}-{hex8}`), so ordering by id
 * is not chronological and the cursor is `(created_at, run_id)`. The client
 * never builds one: it hands back exactly the `meta.nextCursor` the previous
 * response gave it, because the server's cursor carries the database's own
 * microsecond timestamp text and a value reconstructed from a JavaScript `Date`
 * would silently skip rows written inside the same millisecond.
 *
 * **A failed page must not cost the pages already fetched.** `useInfiniteQuery`
 * keeps `data.pages` when `fetchNextPage` rejects, so the grid keeps every row
 * the user was already reading and the failure is an inline message rather than
 * an empty table. `retry` is off for the same reason the canvas puts a "Try
 * again" button in that message: on a triage screen, three silent retries with
 * backoff read as a hung page, and the user is right there to ask again.
 */

export const RUNS_PATH = "/api/v2/runs";

/** One page of history as the screen consumes it. */
export interface RunsPage {
  rows: RunSummaryRow[];
  nextCursor: string | null;
}

export interface RunFilterValues {
  /** One status or several (round 10 wave 2: Overview's "Running now" is running and queued). */
  status?: RunStatus[];
  kind?: RunKind;
  /** Session 11 (D8): what the run did (the Type filter). */
  type?: RunType;
  dataflowId?: string;
  scheduleId?: string;
  /** Round 10 (O1, O2): only the runs the caller started; the server reads who from the session. */
  startedBy?: "me";
  /** ISO-8601 instants; the route validates the offset. */
  from?: string;
  to?: string;
  /**
   * Session 11 (P3, D4): every connection's part of each run. Only with `scheduleId` and a limit
   * of at most 50 (`runsQuerySchema`); a schedule's History asks for it, the Runs list never does.
   */
  include?: RunsInclude;
}

export const RUNS_PAGE_SIZE = 50;

/**
 * How often the run detail screen re-reads a run that is still moving (FR-55).
 *
 * **The page polls whether or not its WebSocket is connected**, and that is not
 * belt and braces. `pg_notify` is issued by ONE place — the progress drain in
 * `runner/steps/progressDrain.ts` — and it carries per-ENTITY progress. Step
 * transitions and the run's own status are written by the step handlers with no
 * notification at all, so a page that stopped polling while the socket was up
 * would sit on "Queued, 0 records" for the whole run and then for ever. That is
 * exactly what it did, for eight minutes, against a run that had already
 * succeeded, before this was split in two.
 *
 * So the socket buys a slower poll, not no poll: it covers the entity rows
 * between reads. When it drops, the interval tightens to cover them too. Both
 * stop the moment the run reaches a terminal state.
 */
export const RUN_POLL_INTERVAL_MS = 5_000;

/** The interval while the socket is carrying entity progress. */
export const RUN_POLL_INTERVAL_LIVE_MS = 15_000;

export function runPath(runId: string): string {
  return `${RUNS_PATH}/${runId}`;
}

export function runEventsPath(runId: string): string {
  return `${runPath(runId)}/events`;
}

/**
 * The keys the run detail queries park on before a run id is known.
 *
 * A TanStack query with `enabled: false` still READS its cache key, so a detail
 * hook that fell back to `RUNS_PATH` would be handed the history's own array
 * and would type it as one run. These are strings nothing ever writes, so a
 * disabled query resolves to `undefined`, which is what "no id yet" means.
 * They are deliberately not paths: `${RUNS_PATH}/` would also work today and
 * reads like a real endpoint, which is how it gets tidied back into a
 * collision. `runScheduleHooks.keys.test.ts` asserts they collide with nothing.
 */
export const RUN_DISABLED_KEY = "run-detail-disabled";
export const RUN_EVENTS_DISABLED_KEY = "run-events-disabled";

/** The list path with its filters, which is also this query's cache key. */
export function runsQueryPath(
  filters: RunFilterValues,
  cursor: string | null,
  limit = RUNS_PAGE_SIZE,
): string {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  for (const [key, value] of Object.entries(filters)) {
    if (key === "status") continue;
    if (value !== undefined && value !== "") params.set(key, String(value));
  }
  if (filters.status?.length) params.set("status", runStatusListParam(filters.status));
  if (cursor) params.set("cursor", cursor);
  return `${RUNS_PATH}?${params.toString()}`;
}

export function useRunsList(
  filters: RunFilterValues = {},
  limit = RUNS_PAGE_SIZE,
  options: {
    /**
     * Session 11 (D8): keep the rows of the previous filter set on screen while the next one
     * loads, so a quick filter applied on change does not blank the grid. Only the Runs list
     * asks: a list whose filter names ONE thing (a connection, a schedule) must never show
     * another's rows, even for a moment.
     */
    keepPreviousRows?: boolean;
  } = {},
): UseInfiniteQueryResult<{ pages: RunsPage[]; pageParams: (string | null)[] }, Error> {
  const { selectedInstance } = useTenantInstance();
  return useInfiniteQuery<RunsPage, Error, { pages: RunsPage[]; pageParams: (string | null)[] }>({
    ...listQueryOptions,
    ...(options.keepPreviousRows ? { placeholderData: keepPreviousData } : {}),
    // The first element stays the literal path so the key reads the same way as
    // every other query in the app; the filters follow it so two filter sets are
    // two cache entries rather than one that flickers between them. The instance closes the key:
    // it rides in a header, so without it two instances share one entry.
    queryKey: [RUNS_PATH, filters, limit, selectedInstance?.id ?? null],
    initialPageParam: null,
    queryFn: async ({ pageParam, signal }) => {
      const envelope = await apiRequestEnvelope<RunSummaryRow[]>(
        runsQueryPath(filters, (pageParam as string | null) ?? null, limit),
        { signal },
      );
      return { rows: envelope.data ?? [], nextCursor: envelope.meta?.nextCursor ?? null };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    retry: false,
  });
}

/**
 * Round 4 (owner, 2026-09-30, items 10a and 4b): the Runs list and a connection's imports page
 * with Previous and Next (`useCursorPager`, `CursorPager`). 20 a page by default, 10/20/50
 * offered (rules.md §4).
 */
export const RUNS_PAGE_SIZES: readonly number[] = [10, 20, 50];
export const RUNS_DEFAULT_PAGE_SIZE = 20;

/** One page of a paged list, as a screen with a pager reads it. */
export interface CursorPage<T> {
  rows: T[];
  nextCursor: string | null;
  /** The whole filtered list (`meta.total`); null from a server that does not say. */
  total: number | null;
}

function pageOf<T>(envelope: {
  data: T[] | null;
  meta?: { nextCursor?: string | null; total?: number };
}): CursorPage<T> {
  const total = envelope.meta?.total;
  return {
    rows: envelope.data ?? [],
    nextCursor: envelope.meta?.nextCursor ?? null,
    total: typeof total === "number" ? total : null,
  };
}

/**
 * One page of `GET /runs` (round 4): the rows, the server's cursor for the page after them and
 * the total under the same filters. The key starts with `RUNS_PATH`, so the live feed's
 * `invalidateQueries([RUNS_PATH])` re-reads whichever page is on screen: the first page takes the
 * new runs, and a later page keeps its rows (the keyset cursor) while its total grows, which is
 * what "N newer runs" counts.
 */
export function useRunsPage(
  filters: RunFilterValues,
  cursor: string | null,
  limit: number,
  options: {
    /**
     * Keep the rows on screen while the next key loads. `true` (the Runs list): across a filter
     * change too, as `useRunsList`. `"sameFilters"` (a connection's imports): only across a page
     * size or a page of the SAME filters, so a list that names one connection never shows
     * another's rows, even for a moment.
     */
    keepPreviousRows?: boolean | "sameFilters";
  } = {},
): UseQueryResult<CursorPage<RunSummaryRow>, Error> & {
  fetchPage: (cursor: string) => Promise<CursorPage<RunSummaryRow>>;
  /** Marks one page stale, so going back to it reads it again ("back to newest"). */
  invalidatePage: (cursor: string | null) => Promise<void>;
} {
  const queryClient = useQueryClient();
  const keyFor = useCallback(
    (at: string | null) => [RUNS_PATH, "page", filters, limit, at ?? ""] as const,
    [filters, limit],
  );
  const read = useCallback(
    async (at: string | null, signal?: AbortSignal): Promise<CursorPage<RunSummaryRow>> =>
      pageOf(
        await apiRequestEnvelope<RunSummaryRow[]>(runsQueryPath(filters, at, limit), { signal }),
      ),
    [filters, limit],
  );
  const sameFilters = JSON.stringify(filters);
  const query = useQuery<CursorPage<RunSummaryRow>, Error>({
    ...listQueryOptions,
    ...(options.keepPreviousRows === true ? { placeholderData: keepPreviousData } : {}),
    ...(options.keepPreviousRows === "sameFilters"
      ? {
          placeholderData: (
            previous: CursorPage<RunSummaryRow> | undefined,
            previousQuery: { queryKey: readonly unknown[] } | undefined,
          ) =>
            previousQuery && JSON.stringify(previousQuery.queryKey[2]) === sameFilters
              ? previous
              : undefined,
        }
      : {}),
    queryKey: keyFor(cursor),
    queryFn: ({ signal }) => read(cursor, signal),
    retry: false,
  });
  const fetchPage = useCallback(
    (at: string) =>
      queryClient.fetchQuery<CursorPage<RunSummaryRow>, Error>({
        // The page on screen is a list read, so the page after it is too (#1398).
        ...listQueryOptions,
        queryKey: keyFor(at),
        queryFn: ({ signal }) => read(at, signal),
        retry: false,
        staleTime: 0,
      }),
    [queryClient, keyFor, read],
  );
  const invalidatePage = useCallback(
    (at: string | null) => queryClient.invalidateQueries({ queryKey: keyFor(at), exact: true }),
    [queryClient, keyFor],
  );
  return { ...query, fetchPage, invalidatePage };
}

/** One run and its steps (FR-53). */
export function useRun(runId: string | null): UseQueryResult<RunDetail> {
  return useQuery<RunDetail>({
    queryKey: [runId ? runPath(runId) : RUN_DISABLED_KEY],
    enabled: Boolean(runId),
    // A run in flight changes every few seconds; caching it for the global 30
    // seconds would make a manual refresh do nothing.
    staleTime: 0,
  });
}

/**
 * The fallback that keeps the run detail page moving when its WebSocket is not
 * (FR-55).
 *
 * Deliberately an explicit interval rather than the query's own
 * `refetchInterval`: the page needs the poll to start and stop on a condition
 * computed from BOTH the run's status and the socket's transport state, and
 * expressing that as a query option meant either two subscriptions to one key
 * or a ref read from inside a callback. This is twelve lines that say what they
 * do, and a test can drive it with fake timers and watch the requests.
 *
 * The effect re-runs when `enabled` changes, so a socket that comes back stops
 * the timer on the next render rather than at the end of the current interval.
 *
 * #1398: a tick while the browser is offline is skipped. A poll that re-reads a list
 * (`listQueryOptions`) would otherwise fail every tick into an error bar; every other
 * read only paused, so skipping it changes nothing for it.
 */
export function usePollWhile(enabled: boolean, intervalMs: number, poll: () => void): void {
  const latest = useRef(poll);
  // Written in an effect, not during render: the interval below reads it when
  // it fires, so the newest callback is what runs without the timer being torn
  // down and rebuilt on every render.
  useEffect(() => {
    latest.current = poll;
  });

  useEffect(() => {
    if (!enabled) return;
    const timer = setInterval(() => {
      if (isOffline()) return;
      latest.current();
    }, intervalMs);
    return () => clearInterval(timer);
  }, [enabled, intervalMs]);
}

export const EVENTS_PAGE_SIZE = 50;

/**
 * The run log's filters (Session 10, P11). Every one goes to the SERVER: a filter applied to the
 * pages already loaded would say "no errors" about a run whose error is on page 2.
 */
export interface RunEventFilterValues {
  level?: "info" | "warn" | "error";
  step?: StepName;
  dataflowId?: string;
  entityName?: string;
  event?: string[];
  /** Newest first unless `oldest`. */
  order?: RunEventOrder;
}

/** The query string of one events page: the non-empty filters, the page size and the cursor. */
export function runEventsQueryString(
  filters: RunEventFilterValues,
  cursor: string | null,
  limit = EVENTS_PAGE_SIZE,
): string {
  const params = new URLSearchParams({ limit: String(limit) });
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === "") continue;
    if (key === "order" && value === "newest") continue;
    if (Array.isArray(value)) {
      for (const item of value) params.append(key, String(item));
    } else {
      params.set(key, String(value));
    }
  }
  if (cursor) params.set("cursor", cursor);
  return params.toString();
}

/**
 * Round 4 (owner, 2026-09-30, item 10c): one page of a run's events, 50 by default and 100 or
 * 200 offered (a log reads longer than a table: rules.md's 10/20/50 is for tables, the exception
 * is taken openly). `meta.total` is this run's count under the same filters. As `useRunsPage`,
 * Next reads the page before the pager moves (`fetchPage`).
 */
export const EVENTS_PAGE_SIZES: readonly number[] = [50, 100, 200];

export function useRunEventsPage(
  runId: string | null,
  enabled: boolean,
  filters: RunEventFilterValues,
  cursor: string | null,
  limit: number = EVENTS_PAGE_SIZE,
): UseQueryResult<CursorPage<RunEventRow>, Error> & {
  fetchPage: (cursor: string) => Promise<CursorPage<RunEventRow>>;
} {
  const queryClient = useQueryClient();
  // The path first, so `invalidateQueries([runEventsPath(id)])` still reaches every filter set
  // and page; the filters, the size and the cursor after it, so each page is its own entry.
  const keyFor = useCallback(
    (at: string | null) =>
      [runId ? runEventsPath(runId) : RUN_EVENTS_DISABLED_KEY, filters, limit, at ?? ""] as const,
    [runId, filters, limit],
  );
  const read = useCallback(
    async (at: string | null, signal?: AbortSignal): Promise<CursorPage<RunEventRow>> =>
      pageOf(
        await apiRequestEnvelope<RunEventRow[]>(
          `${runEventsPath(runId as string)}?${runEventsQueryString(filters, at, limit)}`,
          { signal },
        ),
      ),
    [runId, filters, limit],
  );
  // A new page size or page of the same run and filters keeps the events on screen while it
  // loads (and the pager, with the control the reader is on); a new filter never does.
  const sameLog = JSON.stringify(keyFor(null).slice(0, 2));
  const query = useQuery<CursorPage<RunEventRow>, Error>({
    ...listQueryOptions,
    placeholderData: (previous, previousQuery) =>
      previousQuery && JSON.stringify(previousQuery.queryKey.slice(0, 2)) === sameLog
        ? previous
        : undefined,
    queryKey: keyFor(cursor),
    enabled: Boolean(runId) && enabled,
    queryFn: ({ signal }) => read(cursor, signal),
    retry: false,
  });
  const fetchPage = useCallback(
    (at: string) =>
      queryClient.fetchQuery<CursorPage<RunEventRow>, Error>({
        // The page on screen is a list read, so the page after it is too (#1398).
        ...listQueryOptions,
        queryKey: keyFor(at),
        queryFn: ({ signal }) => read(at, signal),
        retry: false,
        staleTime: 0,
      }),
    [queryClient, keyFor, read],
  );
  return { ...query, fetchPage };
}

/**
 * #1318: how each earlier attempt of a retried step ended. One read of the run's own events,
 * newest first (folded oldest first), only the step endings and the retry requests (`STEP_ATTEMPT_EVENT_CODES`), every
 * page followed up to `STEP_ATTEMPT_MAX_PAGES` of 200 (a longer log leaves its oldest events out,
 * and a step whose earlier attempt is then unknown simply says nothing). Off until a step has
 * been retried, so a run never retried makes no extra request. Re-read while the run moves, and
 * with the run's other events on a run mutation (its key starts with `runEventsPath`).
 */
export function useRunStepAttempts(
  runId: string | null,
  retried: boolean,
  moving: boolean,
): UseQueryResult<StepAttemptHistory, Error> {
  return useQuery<StepAttemptHistory, Error>({
    queryKey: [runId ? runEventsPath(runId) : RUN_EVENTS_DISABLED_KEY, "step-attempts"],
    enabled: Boolean(runId) && retried,
    queryFn: async ({ signal }) => {
      const events: RunEventRow[] = [];
      let cursor: string | null = null;
      for (let page = 0; page < STEP_ATTEMPT_MAX_PAGES; page += 1) {
        const read: CursorPage<RunEventRow> = pageOf(
          await apiRequestEnvelope<RunEventRow[]>(
            `${runEventsPath(runId as string)}?${runEventsQueryString(
              { order: "newest", event: [...STEP_ATTEMPT_EVENT_CODES] },
              cursor,
              200,
            )}`,
            { signal },
          ),
        );
        events.push(...read.rows);
        if (!read.nextCursor) break;
        cursor = read.nextCursor;
      }
      // Read newest first so a cap leaves out the oldest events, then fold oldest first.
      return foldStepAttempts(events.reverse());
    },
    refetchInterval: moving ? RUN_POLL_INTERVAL_MS : false,
    // Another run's attempt endings are never a placeholder for this run's.
    placeholderData: (previous, previousQuery) =>
      runId && previousQuery?.queryKey[0] === runEventsPath(runId) ? previous : undefined,
    retry: false,
    staleTime: 0,
  });
}

/**
 * The run's dbt models and tests (`GET /runs/:runId/transform-results`, Session 10, P11): the
 * transform step's current pass, as the server reads it. Not audited. The run page reads it once,
 * every 15 s while the Transformations view is open on a moving run, and once more whenever the
 * transform step's status or attempt changes or the run turns terminal (the runner writes the
 * model and test rows only as the step finishes). A run mutation (retry, cancel, force reset)
 * invalidates it with the run (`useRunInvalidation`).
 */
export function runTransformResultsPath(runId: string): string {
  return `${runPath(runId)}/transform-results`;
}

/** Where the transform-results query parks before a run id is known (see `RUN_DISABLED_KEY`). */
export const RUN_TRANSFORM_RESULTS_DISABLED_KEY = "run-transform-results-disabled";

export function useRunTransformResults(
  runId: string | null,
  enabled = true,
): UseQueryResult<RunTransformResults> {
  return useQuery<RunTransformResults>({
    queryKey: [runId ? runTransformResultsPath(runId) : RUN_TRANSFORM_RESULTS_DISABLED_KEY],
    enabled: Boolean(runId) && enabled,
    retry: false,
  });
}

/**
 * `GET /runs/:runId/events/export.{csv,jsonl}` with the events filters it honours (Session 10,
 * B13). Every non-empty filter goes on the query string; the page controls never do.
 */
export function runEventsExportPath(
  runId: string,
  format: RunEventExportFormat,
  filters: Partial<RunEventsExportQuery> = {},
): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      for (const item of value) params.append(key, String(item));
    } else {
      params.set(key, String(value));
    }
  }
  const query = params.toString();
  const search = query ? `?${query}` : "";
  return `${runEventsPath(runId)}/export.${format}${search}`;
}

/**
 * Download a run's events as a file. A real request with the session's headers, like the audit
 * export, because the route is audited (`history.export`) and an anonymous anchor would 401. The
 * promise resolves with the saved file's name, for the confirmation the caller shows.
 */
export function useRunEventsExport(): {
  exportEvents: (
    runId: string,
    format: RunEventExportFormat,
    filters?: Partial<RunEventsExportQuery>,
  ) => Promise<string>;
  isExporting: boolean;
} {
  const [isExporting, setExporting] = useState(false);
  // #1078: the file's times are in the reader's display zone, ISO 8601 with its offset, read at
  // the moment of the export (the page does not re-render on `/me` for it).
  const queryClient = useQueryClient();
  const exportEvents = useCallback(
    async (
      runId: string,
      format: RunEventExportFormat,
      filters: Partial<RunEventsExportQuery> = {},
    ): Promise<string> => {
      setExporting(true);
      try {
        const { blob, filename } = await apiDownload(
          runEventsExportPath(runId, format, { ...filters, tz: displayTzNow(queryClient) }),
        );
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
        return filename;
      } finally {
        setExporting(false);
      }
    },
    [queryClient],
  );
  return { exportEvents, isExporting };
}

/** `finishes`: the action ends the run (cancel, force reset), so Last loaded and freshness move. */
function useRunInvalidation(finishes = false): (runId?: string) => Promise<void> {
  const queryClient = useQueryClient();
  return async (runId?: string) => {
    await queryClient.invalidateQueries({ queryKey: [RUNS_PATH] });
    // Without waiting for a socket frame that a down socket would never deliver (#1653).
    if (finishes) invalidateRunHistory(queryClient);
    if (runId) {
      await queryClient.invalidateQueries({ queryKey: [runPath(runId)] });
      await queryClient.invalidateQueries({ queryKey: [runEventsPath(runId)] });
      // Its own key, not under `[runPath(id)]`: keys compare element by element (review r2 B1).
      await queryClient.invalidateQueries({ queryKey: [runTransformResultsPath(runId)] });
    }
  };
}

/**
 * `POST /runs`. The caller owns the Idempotency-Key: `RunNowDialog` mints ONE per open, so a
 * double click or a retry after a lost response answers the same run instead of starting a second
 * one (API-STANDARD §6). Minting a key per call, as this did, made each click a new run.
 */
export function useStartRun(): UseMutationResult<
  StartRunResponse,
  unknown,
  { body: StartRunBody; idempotencyKey: string }
> {
  const invalidate = useRunInvalidation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ body, idempotencyKey }: { body: StartRunBody; idempotencyKey: string }) =>
      apiRequest<StartRunResponse>(RUNS_PATH, {
        method: "POST",
        body,
        headers: { "Idempotency-Key": idempotencyKey },
      }),
    onSuccess: async (result, variables) => {
      await invalidate(result.runId);
      // #1006: a Run now of a whole schedule is now its Last run, so the Schedules list, the
      // schedule's own read and the reliability figures are stale at once (not after 30 s).
      const scheduleId = variables.body.scheduleId;
      if (scheduleId) {
        await queryClient.invalidateQueries({ queryKey: [SCHEDULES_PATH] });
        await queryClient.invalidateQueries({ queryKey: [schedulePath(scheduleId)] });
        await queryClient.invalidateQueries({ queryKey: [RELIABILITY_PATH] });
      }
    },
  });
}

interface RetryStepVariables {
  body: RetryRunBody;
  /** One key per dialog open, suffixed per target (see `RetryFromFailedDialog`). */
  idempotencyKey: string;
}

/** The mutation key, so the page can show its action busy while the dialog retries. */
export function retryStepMutationKey(runId: string): readonly unknown[] {
  return [runPath(runId), "retry"];
}

/**
 * `POST /runs/:runId/retry`. The caller owns the Idempotency-Key, as with `useStartRun`: a double
 * click or a retry after a lost response answers the recorded attempt instead of a 409.
 * `invalidate(runId)` on success is what resumes the page's polling: the refetched run is no
 * longer terminal.
 */
export function useRetryStep(
  runId: string,
): UseMutationResult<RetryRunResult, unknown, RetryStepVariables> {
  const invalidate = useRunInvalidation();
  return useMutation({
    mutationKey: retryStepMutationKey(runId),
    mutationFn: ({ body, idempotencyKey }: RetryStepVariables) =>
      apiRequest<RetryRunResult>(`${runPath(runId)}/retry`, {
        method: "POST",
        body,
        headers: { "Idempotency-Key": idempotencyKey },
      }),
    onSuccess: () => invalidate(runId),
  });
}

interface RetryTablesVariables {
  body: RetryTablesBody;
  /** One key per dialog open, so a double click answers the run the first click started. */
  idempotencyKey: string;
}

/**
 * Product gaps row 2: `POST /runs/:runId/retry-tables`, a NEW run of one connection's tables. The
 * source run is re-read too: its page keeps offering the retry until the new run exists.
 */
export function useRetryTables(
  runId: string,
): UseMutationResult<RetryTablesResult, unknown, RetryTablesVariables> {
  const invalidate = useRunInvalidation();
  return useMutation({
    mutationFn: ({ body, idempotencyKey }: RetryTablesVariables) =>
      apiRequest<RetryTablesResult>(`${runPath(runId)}/retry-tables`, {
        method: "POST",
        body,
        headers: { "Idempotency-Key": idempotencyKey },
      }),
    onSuccess: async (result) => {
      await invalidate(runId);
      await invalidate(result.runId);
    },
  });
}

export interface CancelRunResult {
  runId: string;
  status: RunStatus;
  stoppedExecutions: string[];
  /** F53: executions the platform refused to stop. */
  unstoppedExecutions?: string[];
}

export function useCancelRun(runId: string): UseMutationResult<CancelRunResult, unknown, void> {
  const invalidate = useRunInvalidation(true);
  return useMutation({
    mutationFn: () => apiRequest<CancelRunResult>(`${runPath(runId)}/cancel`, { method: "POST" }),
    onSuccess: () => invalidate(runId),
  });
}

/**
 * The list's cancel, where the run id is only known when the button is pressed.
 * Separate from `useCancelRun` rather than a parameterised version of it: a
 * hook cannot be called per row, and a list that held one mutation per run
 * would break the rules of hooks the first time a page appended.
 */
export function useCancelRunById(): UseMutationResult<CancelRunResult, unknown, string> {
  const invalidate = useRunInvalidation(true);
  return useMutation({
    mutationFn: (runId: string) =>
      apiRequest<CancelRunResult>(`${runPath(runId)}/cancel`, { method: "POST" }),
    onSuccess: (_result, runId) => invalidate(runId),
  });
}

export interface ForceResetResult {
  runId: string;
  status: RunStatus;
  reason: string;
  resetSteps: number;
}

export function useForceResetRun(
  runId: string,
): UseMutationResult<ForceResetResult, unknown, ForceResetBody> {
  const invalidate = useRunInvalidation(true);
  return useMutation({
    mutationFn: (body: ForceResetBody) =>
      apiRequest<ForceResetResult>(`${runPath(runId)}/force-reset`, { method: "POST", body }),
    onSuccess: () => invalidate(runId),
  });
}
